import React, { useState, useEffect } from 'react';
import {
  Users, Plus, Edit2, Trash2, Save, X, Search, Check, AlertCircle,
  Eye, EyeOff, Mail, User, Shield, Key, Lock, UserX, UserCheck,
  BarChart3, FileText, TrendingUp, Calendar, Activity
} from 'lucide-react';
import { UserService } from './apiService';
import './UserManagement.css';

const h = React.createElement;

const UserManagement = () => {
  // États principaux
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [currentView, setCurrentView] = useState('list');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState('');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  // États du formulaire
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    password_confirmation: '',
    role: 'author',
    is_active: true
  });

  // État pour le changement de mot de passe
  const [passwordData, setPasswordData] = useState({
    new_password: '',
    new_password_confirmation: ''
  });

  // États pour les statistiques
  const [userStats, setUserStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);

  const [validationErrors, setValidationErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    loadUsers();
  }, [filterStatus, searchTerm]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const params = {
        status: filterStatus !== 'all' ? filterStatus : undefined,
        search: searchTerm || undefined
      };

      const response = await UserService.getAll(params);
      const usersArray = response.data?.data || response.data || [];
      setUsers(Array.isArray(usersArray) ? usersArray : []);
    } catch (error) {
      console.error('Erreur lors du chargement des utilisateurs:', error);
      setShowErrorMessage('Erreur lors du chargement des utilisateurs.');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const loadUserStats = async (userId) => {
    setLoadingStats(true);
    try {
      const response = await UserService.getStats(userId);
      setUserStats(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques:', error);
      setShowErrorMessage('Erreur lors du chargement des statistiques.');
    } finally {
      setLoadingStats(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      password_confirmation: '',
      role: 'author',
      is_active: true
    });
    setValidationErrors({});
    setSelectedUser(null);
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name || formData.name.trim() === '') {
      errors.name = 'Le nom est requis';
    }

    if (!formData.email || formData.email.trim() === '') {
      errors.email = "L'email est requis";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = "L'email n'est pas valide";
    }

    if (!selectedUser) {
      if (!formData.password || formData.password.length < 8) {
        errors.password = 'Le mot de passe doit contenir au moins 8 caractères';
      }

      if (formData.password !== formData.password_confirmation) {
        errors.password_confirmation = 'Les mots de passe ne correspondent pas';
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreate = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      await UserService.create({
        name: formData.name,
        email: formData.email,
        password: formData.password,
        password_confirmation: formData.password_confirmation,
        role: formData.role,
        is_active: formData.is_active
      });

      setShowSuccessMessage('Utilisateur créé avec succès');
      setTimeout(() => setShowSuccessMessage(''), 3000);
      resetForm();
      setCurrentView('list');
      loadUsers();
    } catch (error) {
      console.error('Erreur lors de la création:', error);
      setShowErrorMessage(
        error.response?.data?.message || 'Erreur lors de la création de l\'utilisateur'
      );
      setTimeout(() => setShowErrorMessage(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedUser) return;
    if (!validateForm()) return;

    setLoading(true);
    try {
      await UserService.update(selectedUser.id, {
        name: formData.name,
        email: formData.email,
        role: formData.role,
        is_active: formData.is_active
      });

      setShowSuccessMessage('Utilisateur modifié avec succès');
      setTimeout(() => setShowSuccessMessage(''), 3000);
      resetForm();
      setCurrentView('list');
      loadUsers();
    } catch (error) {
      console.error('Erreur lors de la modification:', error);
      setShowErrorMessage(
        error.response?.data?.message || 'Erreur lors de la modification de l\'utilisateur'
      );
      setTimeout(() => setShowErrorMessage(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (!selectedUser) return;

    if (!passwordData.new_password || passwordData.new_password.length < 8) {
      setShowErrorMessage('Le mot de passe doit contenir au moins 8 caractères');
      setTimeout(() => setShowErrorMessage(''), 3000);
      return;
    }

    if (passwordData.new_password !== passwordData.new_password_confirmation) {
      setShowErrorMessage('Les mots de passe ne correspondent pas');
      setTimeout(() => setShowErrorMessage(''), 3000);
      return;
    }

    setLoading(true);
    try {
      await UserService.changePassword(selectedUser.id, {
        new_password: passwordData.new_password,
        new_password_confirmation: passwordData.new_password_confirmation
      });

      setShowSuccessMessage('Mot de passe modifié avec succès');
      setTimeout(() => setShowSuccessMessage(''), 3000);
      setShowPasswordModal(false);
      setPasswordData({ new_password: '', new_password_confirmation: '' });
    } catch (error) {
      console.error('Erreur lors du changement de mot de passe:', error);
      setShowErrorMessage('Erreur lors du changement de mot de passe');
      setTimeout(() => setShowErrorMessage(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (user) => {
    setLoading(true);
    try {
      if (user.is_active) {
        await UserService.deactivate(user.id);
        setShowSuccessMessage('Utilisateur désactivé avec succès');
      } else {
        await UserService.activate(user.id);
        setShowSuccessMessage('Utilisateur activé avec succès');
      }
      setTimeout(() => setShowSuccessMessage(''), 3000);
      loadUsers();
    } catch (error) {
      console.error('Erreur:', error);
      setShowErrorMessage(
        error.response?.data?.message || 'Erreur lors de la modification du statut'
      );
      setTimeout(() => setShowErrorMessage(''), 5000);
    } finally {
      setLoading(false);
      setShowDeleteModal(false);
      setUserToDelete(null);
    }
  };

  const handleEdit = (user) => {
    setSelectedUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      password: '',
      password_confirmation: '',
      role: user.role,
      is_active: user.is_active
    });
    setCurrentView('edit');
  };

  const handleViewStats = (user) => {
    setSelectedUser(user);
    setCurrentView('stats');
    loadUserStats(user.id);
  };

  const getRoleBadge = (role) => {
    const badges = {
      admin: { label: 'Administrateur', class: 'um-badge-admin' },
      editor: { label: 'Éditeur', class: 'um-badge-editor' },
      author: { label: 'Auteur', class: 'um-badge-author' }
    };
    const badge = badges[role] || badges.author;
    return h('span', { className: `um-badge ${badge.class}` }, badge.label);
  };

  const getStatusBadge = (isActive) => {
    return isActive 
      ? h('span', { className: 'um-badge um-badge-active' }, 
          h(Check, { size: 14 }), ' Actif')
      : h('span', { className: 'um-badge um-badge-inactive' }, 
          h(X, { size: 14 }), ' Inactif');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const renderListView = () => {
    const filteredUsers = users.filter(user => {
      const matchesSearch = 
        user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = 
        filterStatus === 'all' ||
        (filterStatus === 'active' && user.is_active) ||
        (filterStatus === 'inactive' && !user.is_active);

      return matchesSearch && matchesStatus;
    });

    return h('div', { className: 'um-list-view' },
      // En-tête avec filtres
      h('div', { className: 'um-list-header' },
        h('div', { className: 'um-list-header-top' },
          h('h2', null,
            h(Users, { size: 24 }),
            'Gestion des utilisateurs'
          ),
          h('button', {
            className: 'um-btn um-btn-primary',
            onClick: () => {
              resetForm();
              setCurrentView('create');
            }
          },
            h(Plus, { size: 20 }),
            'Nouvel utilisateur'
          )
        ),

        h('div', { className: 'um-filters' },
          h('div', { className: 'um-search-box' },
            h(Search, { size: 20 }),
            h('input', {
              type: 'text',
              placeholder: 'Rechercher par nom ou email...',
              value: searchTerm,
              onChange: (e) => setSearchTerm(e.target.value)
            })
          ),

          h('select', {
            value: filterStatus,
            onChange: (e) => setFilterStatus(e.target.value),
            className: 'um-filter-select'
          },
            h('option', { value: 'all' }, 'Tous les statuts'),
            h('option', { value: 'active' }, 'Actifs'),
            h('option', { value: 'inactive' }, 'Inactifs')
          )
        )
      ),

      // Statistiques globales
      h('div', { className: 'um-stats-grid' },
        h('div', { className: 'um-stat-card' },
          h('div', { className: 'um-stat-icon um-stat-total' },
            h(Users, { size: 24 })
          ),
          h('div', { className: 'um-stat-content' },
            h('h3', null, users.length),
            h('p', null, 'Total utilisateurs')
          )
        ),

        h('div', { className: 'um-stat-card' },
          h('div', { className: 'um-stat-icon um-stat-active' },
            h(UserCheck, { size: 24 })
          ),
          h('div', { className: 'um-stat-content' },
            h('h3', null, users.filter(u => u.is_active).length),
            h('p', null, 'Utilisateurs actifs')
          )
        ),

        h('div', { className: 'um-stat-card' },
          h('div', { className: 'um-stat-icon um-stat-admin' },
            h(Shield, { size: 24 })
          ),
          h('div', { className: 'um-stat-content' },
            h('h3', null, users.filter(u => u.role === 'admin').length),
            h('p', null, 'Administrateurs')
          )
        ),

        h('div', { className: 'um-stat-card' },
          h('div', { className: 'um-stat-icon um-stat-articles' },
            h(FileText, { size: 24 })
          ),
          h('div', { className: 'um-stat-content' },
            h('h3', null, users.reduce((sum, user) => sum + (user.published_articles_count || 0), 0)),
            h('p', null, 'Articles publiés')
          )
        )
      ),

      // Table des utilisateurs
      h('div', { className: 'um-table-container' },
        loading ? h('div', { className: 'um-loading' }, 'Chargement...') :
        filteredUsers.length === 0 ? h('div', { className: 'um-empty-state' },
          h(Users, { size: 48 }),
          h('p', null, 'Aucun utilisateur trouvé')
        ) :
        h('table', { className: 'um-table' },
          h('thead', null,
            h('tr', null,
              h('th', null, 'Utilisateur'),
              h('th', null, 'Rôle'),
              h('th', null, 'Statut'),
              h('th', null, 'Articles publiés'),
              h('th', null, 'Total articles'),
              h('th', null, 'Inscrit le'),
              h('th', null, 'Actions')
            )
          ),
          h('tbody', null,
            filteredUsers.map(user =>
              h('tr', { key: user.id },
                h('td', null,
                  h('div', { className: 'um-user-info' },
                    h('div', { className: 'um-user-avatar' },
                      h(User, { size: 20 })
                    ),
                    h('div', null,
                      h('div', { className: 'um-user-name' }, user.name),
                      h('div', { className: 'um-user-email' }, user.email)
                    )
                  )
                ),
                h('td', null, getRoleBadge(user.role)),
                h('td', null, getStatusBadge(user.is_active)),
                h('td', null,
                  h('span', { className: 'um-count-badge um-count-published' },
                    user.published_articles_count || 0
                  )
                ),
                h('td', null,
                  h('span', { className: 'um-count-badge um-count-total' },
                    user.total_articles_count || 0
                  )
                ),
                h('td', null, formatDate(user.created_at)),
                h('td', null,
                  h('div', { className: 'um-actions' },
                    h('button', {
                      className: 'um-action-btn um-action-stats',
                      onClick: () => handleViewStats(user),
                      title: 'Voir les statistiques'
                    }, h(BarChart3, { size: 18 })),
                    h('button', {
                      className: 'um-action-btn um-action-edit',
                      onClick: () => handleEdit(user),
                      title: 'Modifier'
                    }, h(Edit2, { size: 18 })),
                    h('button', {
                      className: 'um-action-btn um-action-password',
                      onClick: () => {
                        setSelectedUser(user);
                        setShowPasswordModal(true);
                      },
                      title: 'Changer le mot de passe'
                    }, h(Key, { size: 18 })),
                    h('button', {
                      className: `um-action-btn ${user.is_active ? 'um-action-deactivate' : 'um-action-activate'}`,
                      onClick: () => {
                        setUserToDelete(user);
                        setShowDeleteModal(true);
                      },
                      title: user.is_active ? 'Désactiver' : 'Activer'
                    }, user.is_active ? h(UserX, { size: 18 }) : h(UserCheck, { size: 18 }))
                  )
                )
              )
            )
          )
        )
      )
    );
  };

  const renderFormView = () => {
    const isEdit = currentView === 'edit';

    return h('div', { className: 'um-form-view' },
      h('div', { className: 'um-form-header' },
        h('h2', null,
          isEdit ? [h(Edit2, { size: 24, key: 'icon' }), 'Modifier l\'utilisateur'] :
                   [h(Plus, { size: 24, key: 'icon' }), 'Nouvel utilisateur']
        ),
        h('button', {
          className: 'um-btn um-btn-secondary',
          onClick: () => {
            resetForm();
            setCurrentView('list');
          }
        },
          h(X, { size: 20 }),
          'Annuler'
        )
      ),

      h('div', { className: 'um-form-content' },
        h('div', { className: 'um-form-section' },
          h('h3', null, 'Informations de base'),

          h('div', { className: 'um-form-grid' },
            h('div', { className: 'um-form-group' },
              h('label', null,
                h(User, { size: 18 }),
                'Nom complet *'
              ),
              h('input', {
                type: 'text',
                value: formData.name,
                onChange: (e) => setFormData({ ...formData, name: e.target.value }),
                placeholder: 'Jean Dupont',
                className: validationErrors.name ? 'um-input-error' : ''
              }),
              validationErrors.name && h('span', { className: 'um-error-message' }, validationErrors.name)
            ),

            h('div', { className: 'um-form-group' },
              h('label', null,
                h(Mail, { size: 18 }),
                'Email *'
              ),
              h('input', {
                type: 'email',
                value: formData.email,
                onChange: (e) => setFormData({ ...formData, email: e.target.value }),
                placeholder: 'jean.dupont@example.com',
                className: validationErrors.email ? 'um-input-error' : ''
              }),
              validationErrors.email && h('span', { className: 'um-error-message' }, validationErrors.email)
            )
          ),

          !isEdit && h('div', { className: 'um-form-grid' },
            h('div', { className: 'um-form-group' },
              h('label', null,
                h(Lock, { size: 18 }),
                'Mot de passe *'
              ),
              h('div', { className: 'um-password-input' },
                h('input', {
                  type: showPassword ? 'text' : 'password',
                  value: formData.password,
                  onChange: (e) => setFormData({ ...formData, password: e.target.value }),
                  placeholder: 'Minimum 8 caractères',
                  className: validationErrors.password ? 'um-input-error' : ''
                }),
                h('button', {
                  type: 'button',
                  className: 'um-password-toggle',
                  onClick: () => setShowPassword(!showPassword)
                }, showPassword ? h(EyeOff, { size: 18 }) : h(Eye, { size: 18 }))
              ),
              validationErrors.password && h('span', { className: 'um-error-message' }, validationErrors.password)
            ),

            h('div', { className: 'um-form-group' },
              h('label', null,
                h(Lock, { size: 18 }),
                'Confirmer le mot de passe *'
              ),
              h('input', {
                type: showPassword ? 'text' : 'password',
                value: formData.password_confirmation,
                onChange: (e) => setFormData({ ...formData, password_confirmation: e.target.value }),
                placeholder: 'Confirmer le mot de passe',
                className: validationErrors.password_confirmation ? 'um-input-error' : ''
              }),
              validationErrors.password_confirmation && 
                h('span', { className: 'um-error-message' }, validationErrors.password_confirmation)
            )
          )
        ),

        h('div', { className: 'um-form-section' },
          h('h3', null, 'Rôle et permissions'),

          h('div', { className: 'um-form-grid' },
            h('div', { className: 'um-form-group' },
              h('label', null,
                h(Shield, { size: 18 }),
                'Rôle'
              ),
              h('select', {
                value: formData.role,
                onChange: (e) => setFormData({ ...formData, role: e.target.value })
              },
                h('option', { value: 'author' }, 'Auteur'),
                h('option', { value: 'editor' }, 'Éditeur'),
                h('option', { value: 'admin' }, 'Administrateur')
              ),
              h('small', { className: 'um-help-text' },
                formData.role === 'admin' ? 'Accès complet à toutes les fonctionnalités' :
                formData.role === 'editor' ? 'Peut créer et gérer tous les articles' :
                'Peut créer et gérer ses propres articles'
              )
            ),

            h('div', { className: 'um-form-group' },
              h('label', { className: 'um-checkbox-label' },
                h('input', {
                  type: 'checkbox',
                  checked: formData.is_active,
                  onChange: (e) => setFormData({ ...formData, is_active: e.target.checked })
                }),
                h('span', null, 'Compte actif')
              ),
              h('small', { className: 'um-help-text' },
                formData.is_active
                  ? "L'utilisateur peut se connecter et accéder au backoffice"
                  : "L'utilisateur ne peut pas se connecter"
              )
            )
          )
        ),

        h('div', { className: 'um-form-actions' },
          h('button', {
            className: 'um-btn um-btn-secondary',
            onClick: () => {
              resetForm();
              setCurrentView('list');
            }
          }, 'Annuler'),
          h('button', {
            className: 'um-btn um-btn-primary',
            onClick: isEdit ? handleUpdate : handleCreate,
            disabled: loading
          },
            h(Save, { size: 20 }),
            isEdit ? 'Enregistrer les modifications' : 'Créer l\'utilisateur'
          )
        )
      )
    );
  };

  const renderStatsView = () => {
    if (!selectedUser) return null;

    return h('div', { className: 'um-stats-view' },
      h('div', { className: 'um-stats-header' },
        h('div', null,
          h('h2', null,
            h(BarChart3, { size: 24 }),
            'Statistiques de ', selectedUser.name
          ),
          h('p', { className: 'um-stats-subtitle' }, selectedUser.email)
        ),
        h('button', {
          className: 'um-btn um-btn-secondary',
          onClick: () => {
            setCurrentView('list');
            setSelectedUser(null);
            setUserStats(null);
          }
        },
          h(X, { size: 20 }),
          'Fermer'
        )
      ),

      loadingStats ? h('div', { className: 'um-loading' }, 'Chargement des statistiques...') :
      userStats ? h(React.Fragment, null,
        h('div', { className: 'um-stats-cards' },
          h('div', { className: 'um-stat-card um-stat-large' },
            h('div', { className: 'um-stat-icon um-stat-total' },
              h(FileText, { size: 32 })
            ),
            h('div', { className: 'um-stat-content' },
              h('h3', null, userStats.total_articles),
              h('p', null, 'Total des articles')
            )
          ),

          h('div', { className: 'um-stat-card um-stat-large' },
            h('div', { className: 'um-stat-icon um-stat-published' },
              h(Check, { size: 32 })
            ),
            h('div', { className: 'um-stat-content' },
              h('h3', null, userStats.published_articles),
              h('p', null, 'Articles publiés')
            )
          ),

          h('div', { className: 'um-stat-card um-stat-large' },
            h('div', { className: 'um-stat-icon um-stat-draft' },
              h(Edit2, { size: 32 })
            ),
            h('div', { className: 'um-stat-content' },
              h('h3', null, userStats.draft_articles),
              h('p', null, 'Brouillons')
            )
          ),

          h('div', { className: 'um-stat-card um-stat-large' },
            h('div', { className: 'um-stat-icon um-stat-views' },
              h(TrendingUp, { size: 32 })
            ),
            h('div', { className: 'um-stat-content' },
              h('h3', null, userStats.total_views),
              h('p', null, 'Vues totales')
            )
          )
        ),

        userStats.recent_articles && userStats.recent_articles.length > 0 &&
        h('div', { className: 'um-recent-articles' },
          h('h3', null,
            h(Activity, { size: 20 }),
            'Articles récents'
          ),
          h('div', { className: 'um-articles-list' },
            userStats.recent_articles.map(article =>
              h('div', { key: article.id, className: 'um-article-item' },
                h('div', { className: 'um-article-info' },
                  h('h4', null, article.title),
                  h('div', { className: 'um-article-meta' },
                    h('span', null,
                      h(Calendar, { size: 14 }),
                      formatDate(article.publish_date)
                    ),
                    h('span', null,
                      h(Eye, { size: 14 }),
                      article.views || 0, ' vues'
                    )
                  )
                ),
                h('div', { className: 'um-article-status' },
                  article.status === 'published' && h('span', { className: 'um-badge um-badge-published' }, 'Publié'),
                  article.status === 'draft' && h('span', { className: 'um-badge um-badge-draft' }, 'Brouillon'),
                  article.status === 'scheduled' && h('span', { className: 'um-badge um-badge-scheduled' }, 'Programmé')
                )
              )
            )
          )
        )
      ) : h('div', { className: 'um-empty-state' },
        h(AlertCircle, { size: 48 }),
        h('p', null, 'Impossible de charger les statistiques')
      )
    );
  };

  return h('div', { className: 'user-management' },
    showSuccessMessage && h('div', { className: 'um-success-message' },
      h(Check, { size: 20 }),
      showSuccessMessage
    ),

    showErrorMessage && h('div', { className: 'um-error-message-banner' },
      h(AlertCircle, { size: 20 }),
      showErrorMessage,
      h('button', { onClick: () => setShowErrorMessage('') },
        h(X, { size: 16 })
      )
    ),

    currentView === 'list' && renderListView(),
    (currentView === 'create' || currentView === 'edit') && renderFormView(),
    currentView === 'stats' && renderStatsView(),

    // Modal changement de mot de passe
    showPasswordModal && h('div', {
      className: 'um-modal-overlay',
      onClick: () => setShowPasswordModal(false)
    },
      h('div', {
        className: 'um-modal',
        onClick: (e) => e.stopPropagation()
      },
        h('div', { className: 'um-modal-header' },
          h('h3', null,
            h(Key, { size: 24 }),
            'Changer le mot de passe'
          ),
          h('button', {
            className: 'um-btn-close',
            onClick: () => setShowPasswordModal(false)
          }, h(X, { size: 20 }))
        ),

        h('div', { className: 'um-modal-body' },
          h('p', { className: 'um-modal-info' },
            'Modifier le mot de passe de ',
            h('strong', null, selectedUser?.name)
          ),

          h('div', { className: 'um-form-group' },
            h('label', null,
              h(Lock, { size: 18 }),
              'Nouveau mot de passe'
            ),
            h('input', {
              type: 'password',
              value: passwordData.new_password,
              onChange: (e) => setPasswordData({ ...passwordData, new_password: e.target.value }),
              placeholder: 'Minimum 8 caractères'
            })
          ),

          h('div', { className: 'um-form-group' },
            h('label', null,
              h(Lock, { size: 18 }),
              'Confirmer le mot de passe'
            ),
            h('input', {
              type: 'password',
              value: passwordData.new_password_confirmation,
              onChange: (e) => setPasswordData({ ...passwordData, new_password_confirmation: e.target.value }),
              placeholder: 'Confirmer le mot de passe'
            })
          )
        ),

        h('div', { className: 'um-modal-footer' },
          h('button', {
            className: 'um-btn um-btn-secondary',
            onClick: () => setShowPasswordModal(false)
          }, 'Annuler'),
          h('button', {
            className: 'um-btn um-btn-primary',
            onClick: handleChangePassword,
            disabled: loading
          },
            h(Save, { size: 20 }),
            'Modifier le mot de passe'
          )
        )
      )
    ),

    // Modal confirmation désactivation/activation
    showDeleteModal && userToDelete && h('div', {
      className: 'um-modal-overlay',
      onClick: () => setShowDeleteModal(false)
    },
      h('div', {
        className: 'um-modal',
        onClick: (e) => e.stopPropagation()
      },
        h('div', { className: 'um-modal-header' },
          h('h3', null,
            userToDelete.is_active ? [
              h(UserX, { size: 24, key: 'icon' }),
              'Désactiver l\'utilisateur'
            ] : [
              h(UserCheck, { size: 24, key: 'icon' }),
              'Activer l\'utilisateur'
            ]
          ),
          h('button', {
            className: 'um-btn-close',
            onClick: () => setShowDeleteModal(false)
          }, h(X, { size: 20 }))
        ),

        h('div', { className: 'um-modal-body' },
          h('p', { className: 'um-modal-warning' },
            userToDelete.is_active ? [
              'Êtes-vous sûr de vouloir désactiver ',
              h('strong', { key: 'name' }, userToDelete.name),
              ' ?',
              h('br', { key: 'br1' }),
              h('br', { key: 'br2' }),
              'L\'utilisateur ne pourra plus se connecter au backoffice.'
            ] : [
              'Êtes-vous sûr de vouloir activer ',
              h('strong', { key: 'name' }, userToDelete.name),
              ' ?',
              h('br', { key: 'br1' }),
              h('br', { key: 'br2' }),
              'L\'utilisateur pourra à nouveau se connecter au backoffice.'
            ]
          )
        ),

        h('div', { className: 'um-modal-footer' },
          h('button', {
            className: 'um-btn um-btn-secondary',
            onClick: () => setShowDeleteModal(false)
          }, 'Annuler'),
          h('button', {
            className: `um-btn ${userToDelete.is_active ? 'um-btn-danger' : 'um-btn-success'}`,
            onClick: () => handleToggleActive(userToDelete),
            disabled: loading
          },
            userToDelete.is_active ? [
              h(UserX, { size: 20, key: 'icon' }),
              'Désactiver'
            ] : [
              h(UserCheck, { size: 20, key: 'icon' }),
              'Activer'
            ]
          )
        )
      )
    )
  );
};

export default UserManagement;
